import { AriaAttributes, useMemo } from 'react';
import type { AccessibilityProps } from 'react-native';

interface HTMLA11y extends Partial<AriaAttributes> {
    role?: string;
    title?: string;
}

const useAccessibilityProps = (nativeProps: AccessibilityProps): AriaAttributes => {
    return useMemo(() => {
        const aria: HTMLA11y = {};
        for (const prop in nativeProps) {
            switch (prop) {
                case 'accessibilityLabel':
                    aria['aria-label'] = nativeProps[prop];
                    break;
                case 'accessibilityRole':
                    aria.role = nativeProps[prop];
                    break;
                case 'accessibilityState': {
                    const state = nativeProps[prop];
                    if (!state) {
                        break;
                    }

                    aria['aria-checked'] = state.checked && 'true';
                    aria['aria-disabled'] = state.disabled && 'true';
                    aria['aria-selected'] = state.selected && 'true';
                    aria['aria-busy'] = state.busy && 'true';
                    aria['aria-expanded'] = state.expanded && 'true';
                    break;
                }
                case 'accessibilityValue':
                    {
                        const value = nativeProps[prop];
                        if (!value) {
                            continue;
                        }
                        for (const av in value) {
                            switch (av) {
                                case 'min':
                                    aria['aria-valuemin'] = value[av];
                                    break;
                                case 'max':
                                    aria['aria-valuemax'] = value[av];
                                    break;
                                case 'text':
                                    aria['aria-valuetext'] = value[av];
                                    break;
                                case 'now':
                                    aria['aria-valuenow'] = value[av];
                                    break;
                            }
                        }
                    }
                    break;
                case 'accessibilityHint':
                    aria.title = nativeProps[prop];
                    break;
                case 'accessibilityLiveRegion': {
                    const live = nativeProps[prop];
                    aria['aria-live'] = live === 'none' ? 'off' : live;
                    break;
                }
                case 'accessibilityViewIsModal':
                    aria['aria-modal'] = nativeProps[prop] ? 'true' : 'false';
                    break;
                case 'accessibilityElementsHidden':
                    aria['aria-hidden'] = nativeProps[prop] ? 'true' : 'false';
                    break;
                default:
                    break;
            }
        }
        return aria;
    }, [nativeProps]);
};

export default useAccessibilityProps;
